import { focusElement, focusableElements, setPanelVisibility } from './shared';

export default class DesktopNavigation {
    constructor(header, { onOpen, onStateChange }) {
        this.header = header;
        this.navigation = header.querySelector('[data-desktop-navigation]');
        this.onOpen = onOpen;
        this.onStateChange = onStateChange;
        this.activeEntry = null;
        this.enabled = false;

        if (!this.navigation) {
            return;
        }

        this.entries = Array.from(this.navigation.querySelectorAll('[data-desktop-entry]'));
        this.entries.forEach((entry) => {
            entry.addEventListener('pointerenter', (event) => {
                if (event.pointerType === 'mouse' && this.enabled) {
                    this.open(entry);
                }
            });
            entry.addEventListener('pointerleave', (event) => {
                if (event.pointerType === 'mouse' && !entry.contains(document.activeElement)) {
                    this.close(entry);
                }
            });
            entry.addEventListener('focusin', (event) => {
                const panel = entry.querySelector('[data-desktop-panel]');

                if (this.enabled && event.target.matches('a') && !panel?.contains(event.target)) {
                    this.open(entry);
                }
            });
            entry.addEventListener('focusout', (event) => {
                if (!entry.contains(event.relatedTarget) && !entry.matches(':hover')) {
                    this.close(entry);
                }
            });
        });

        this.navigation.addEventListener('click', (event) => {
            if (!this.enabled) {
                return;
            }

            const toggle = event.target.closest('[data-desktop-toggle]');
            const tab = event.target.closest('[data-desktop-tab]');

            if (toggle) {
                const entry = toggle.closest('[data-desktop-entry]');

                if (this.activeEntry === entry) {
                    this.close(entry);
                } else {
                    this.open(entry);
                }
            } else if (tab) {
                this.selectTab(tab);
            }
        });

        this.navigation.addEventListener('keydown', (event) => this.onKeydown(event));
        document.addEventListener('pointerdown', (event) => {
            if (this.activeEntry && !this.activeEntry.contains(event.target)) {
                this.closeAll();
            }
        });
    }

    setEnabled(enabled) {
        this.enabled = enabled;

        if (!this.navigation) {
            return;
        }

        if (!enabled) {
            this.closeAll({ immediate: true });
        }

        this.navigation.inert = !enabled;
        this.navigation.setAttribute('aria-hidden', String(!enabled));
    }

    open(entry) {
        const toggle = entry.querySelector('[data-desktop-toggle]');
        const panel = entry.querySelector('[data-desktop-panel]');

        if (!this.enabled || !toggle || !panel || this.activeEntry === entry) {
            return;
        }

        this.closeAll({ immediate: true });
        this.onOpen?.();
        this.activeEntry = entry;
        entry.dataset.open = 'true';
        toggle.setAttribute('aria-expanded', 'true');
        this.resetTabs(panel);
        setPanelVisibility(panel, true, { distance: 0 });
        this.onStateChange?.();
    }

    close(entry, { immediate = false, restoreFocus = false, moveFocus = true } = {}) {
        if (!entry || this.activeEntry !== entry) {
            return;
        }

        const toggle = entry.querySelector('[data-desktop-toggle]');
        const panel = entry.querySelector('[data-desktop-panel]');
        this.activeEntry = null;
        entry.dataset.open = 'false';
        toggle?.setAttribute('aria-expanded', 'false');

        if (restoreFocus || (moveFocus && panel?.contains(document.activeElement))) {
            focusElement(toggle);
        }

        setPanelVisibility(panel, false, { immediate, distance: 0 });
        this.onStateChange?.();
    }

    closeAll(options = {}) {
        this.close(this.activeEntry, options);
    }

    resetTabs(panel) {
        panel.querySelectorAll('[role="tablist"]').forEach((list) => {
            const first = list.querySelector('[data-desktop-tab]');

            if (first) {
                this.selectTab(first, false);
            }
        });
    }

    selectTab(tab, animate = true) {
        const list = tab.closest('[role="tablist"]');

        if (!list) {
            return;
        }

        list.querySelectorAll('[data-desktop-tab]').forEach((candidate) => {
            const panel = document.getElementById(candidate.getAttribute('aria-controls'));
            const active = candidate === tab;
            candidate.setAttribute('aria-selected', String(active));
            candidate.tabIndex = active ? 0 : -1;

            if (panel && this.navigation.contains(panel)) {
                setPanelVisibility(panel, active, { immediate: !active || !animate, distance: 0 });
            }
        });
    }

    onKeydown(event) {
        if (!this.enabled) {
            return;
        }

        if (event.key === 'Escape' && this.activeEntry) {
            event.preventDefault();
            event.stopPropagation();
            this.closeAll({ restoreFocus: true });
            return;
        }

        const tab = event.target.closest('[data-desktop-tab]');

        if (
            tab &&
            ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)
        ) {
            const tabs = Array.from(
                tab.closest('[role="tablist"]').querySelectorAll('[data-desktop-tab]')
            );
            const index = tabs.indexOf(tab);
            let next = index;

            if (event.key === 'Home') {
                next = 0;
            } else if (event.key === 'End') {
                next = tabs.length - 1;
            } else {
                next =
                    (index +
                        (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) +
                        tabs.length) %
                    tabs.length;
            }

            event.preventDefault();
            this.selectTab(tabs[next]);
            focusElement(tabs[next]);
            return;
        }

        const toggle = event.target.closest('[data-desktop-toggle]');

        if (toggle && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            const entry = toggle.closest('[data-desktop-entry]');
            this.open(entry);
            const elements = focusableElements(entry.querySelector('[data-desktop-panel]'));
            focusElement(event.key === 'ArrowUp' ? elements[elements.length - 1] : elements[0]);
        }
    }
}
